import { HttpParams } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isEqual } from 'lodash-es';
import { Subscription } from 'rxjs';
import { ActionResponse } from '../../../shared/_models/action-response.model';
import { SaveActionResponse } from '../../../shared/_models/save-action-response.model';
import { ActionService } from '../../../shared/_services/action.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-cbc-beps-countries',
    templateUrl: './beps-countries.component.html',
    styleUrls: ['./beps-countries.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BepsCountriesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  countries: any[] = [];
  taxResidenceCountries: any[] = [];
  dataLoaded = false;
  minimise = false;
  private isLoading = false;

  request: {
    tin: string;
    country: string;
    countryId: number | null;
    taxResidence: string;
    taxResidenceId: number | null;
    leName: string;
    leid: string;
    taxYear: string;
  };
  preChangeRequest: {
    tin: string;
    country: string;
    countryId: number | null;
    taxResidence: string;
    taxResidenceId: number | null;
    leName: string;
    leid: string;
    taxYear: string;
  };

  GENERAL_CONFIG: any = {};
  globalParams: any = {};
  filterParams: any = {};
  rowData: any = {};

  constructor(
    private actionService: ActionService,
    private alertService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {
    this.request = { leid: '', tin: '', country: '', countryId: null, taxResidence: '', taxResidenceId: null, leName: '', taxYear: '' };
    this.preChangeRequest = { leid: '', tin: '', country: '', countryId: null, taxResidence: '', taxResidenceId: null, leName: '', taxYear: '' };
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  private init(): void {
    try {
      if (this.isLoading || this.dataLoaded) return;

      // Pre-fill request fields from rowData when the bridge has populated it.
      // The countries dropdowns must still load even when rowData is missing
      // (legacy row shapes / add-mode), otherwise the modal stays on a spinner.
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.request.tin = this.pickField(['CBYC_TIN', 'CbyC_TIN', 'cbyc_tin', 'TIN', 'tin']);
        this.request.leName = this.pickField(['LE_NAME', 'LE_Name', 'le_name', 'LEName']);
        this.request.country = this.pickField(['COUNTRY_INCORP', 'Country_Incorp_', 'COUNTRY_INCORP_', 'country_incorp', 'COUNTRY_OF_INCORP', 'COUNTRY']);
        this.request.taxResidence = this.pickField(['TAX_RESIDENCE', 'Tax_Residence', 'tax_residence', 'TAX_RES']);
        this.request.leid = this.pickField(['LEID', 'leid', 'LE_ID']);
        this.request.taxYear = this.filterParams?.tax_year || this.globalParams?.tax_year || '';
        this.preChangeRequest = { ...this.request };
      }

      this.loadCountries();
    } catch (e) {
      console.error('Error in BepsCountries init:', e);
      this.dataLoaded = true;
      this.cdr.markForCheck();
    }
  }

  private pickField(candidates: string[]): string {
    for (const key of candidates) {
      const v = this.rowData?.[key];
      if (v !== undefined && v !== null && v !== '') {
        return String(v);
      }
    }
    return '';
  }

  private loadCountries(): void {
    const baseUrl = this.GENERAL_CONFIG.api || this.GENERAL_CONFIG.base_url || '/gtw';
    const clientKey = this.globalParams?.client_key || 0;
    const params = new HttpParams({});

    this.isLoading = true;

    this.actionService.getActionData(baseUrl, 33311, clientKey, params).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ActionResponse) => {
          const list = (res?.jsonObject || []).map((item: { ID: number, VALUE: string }) => ({
            value: item.ID,
            label: item.VALUE
          }));
          this.countries = list;
          this.taxResidenceCountries = list;

          const norm = (s: string) => (s || '').toString().trim().toLowerCase();
          const selectedCountry = list.find((item: any) => norm(item.label) === norm(this.request.country));
          this.request.countryId = selectedCountry?.value ?? null;

          const selectedTaxRes = list.find((item: any) => norm(item.label) === norm(this.request.taxResidence));
          this.request.taxResidenceId = selectedTaxRes?.value ?? null;

          this.preChangeRequest = { ...this.request };
          this.dataLoaded = true;
          this.isLoading = false;
          this.cdr.detectChanges();
          // Template-driven [(ngModel)] controls inside *ngIf register
          // asynchronously (NgModel uses Promise.resolve to write the
          // initial value). Inside an Angular Elements host this microtask
          // can land after the parent's CD tick, leaving inputs visually
          // empty even though the bound model is correct. Re-assigning the
          // request object on the next tick forces NgModel to push the
          // values into the freshly-registered controls.
          setTimeout(() => {
            this.request = { ...this.request };
            this.cdr.detectChanges();
          }, 0);
        },
        error: (err) => {
          console.error('Error loading BEPS countries list:', err);
          this.dataLoaded = true;
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  onCountryChange(val: number): void {
    const found = this.countries.find((c: any) => c.value === val);
    this.request.country = found?.label || '';
    this.request.countryId = found?.value ?? null;
  }

  onTaxResidenceChange(val: number): void {
    const found = this.taxResidenceCountries.find((c: any) => c.value === val);
    this.request.taxResidence = found?.label || '';
    this.request.taxResidenceId = found?.value ?? null;
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  updateCountry(): void {
    if (isEqual(this.request, this.preChangeRequest)) {
      this.alertService.openSnackBar('No changes made', 'gtw-snackbar--orange');
      return;
    }

    const baseUrl = this.GENERAL_CONFIG.api || this.GENERAL_CONFIG.base_url || '/gtw';
    const clientKey = this.globalParams?.client_key || 0;

    const params = new HttpParams({
      fromObject: {
        leid: this.request.leid,
        tax_year: this.request.taxYear,
        tin: this.request.tin === this.preChangeRequest.tin ? '' : this.request.tin,
        country: this.request.country === this.preChangeRequest.country ? '' : this.request.country,
        country_id: this.request.countryId === this.preChangeRequest.countryId ? '' : String(this.request.countryId ?? ''),
        tax_residence: this.request.taxResidence === this.preChangeRequest.taxResidence ? '' : this.request.taxResidence,
        tax_residence_id: this.request.taxResidenceId === this.preChangeRequest.taxResidenceId ? '' : String(this.request.taxResidenceId ?? ''),
        leName: this.request.leName === this.preChangeRequest.leName ? '' : this.request.leName
      }
    });

    this.actionService.saveActionData(baseUrl, 33312, clientKey, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: SaveActionResponse) => {
      if (response.callSuccess === '1') {
        this.alertService.openSnackBar('Changes saved successfully', 'gtw-snackbar--green');
        this.requestGridUpdate.emit('refresh');
      } else {
        this.alertService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
      }
    });
  }
}
